import type { PrimeVueConfiguration } from 'primevue/config'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import DialogService from 'primevue/dialogservice'
import ConfirmationService from 'primevue/confirmationservice'

import Ripple from 'primevue/ripple'
import Aura from '@primeuix/themes/aura'
import type { UserModule } from '@/types'
import { definePreset } from '@primeuix/themes'
import { ComponentsDesignTokens } from '@primeuix/themes/types'
import PrimeVuePassThroughOptions from '@/configs/PrimeVuePassThroughOptions'

const componentTokens: ComponentsDesignTokens = {
  button: {
    colorScheme: {
      light: {
        root: {
          primary: {
            hoverBackground: '{primary.400}',
            hoverBorderColor: '{primary.400}',
          },
        },
        outlined: {
          primary: {
            borderColor: '{gray.300}',
          },
          secondary: {
            borderColor: '{gray.500}',
            color: '{gray.500}',
          },
          warn: {
            borderColor: '{orange.500}',
            color: '{orange.500}',
          },
          danger: {
            borderColor: '{red.500}',
            color: '{red.500}',
          },
          info: {
            borderColor: '{blue.500}',
            color: '{blue.500}',
          },
        },
      },
    },
  },
  badge: {
    colorScheme: {
      light: {
        secondary: {
          background: '{zinc.200}',
          color: '{zinc.600}',
        },
      },
    },
  },
  message: {
    colorScheme: {
      light: {
        info: {
          color: '{primary.700}',
        },
      },
    },
  },
  radiobutton: {
    root: {
      checkedBackground: 'white',
      checkedBorderColor: '{primary.400}',

      checkedHoverBackground: 'white',
      hoverBorderColor: '{primary.400}',
    },
    icon: {
      checkedHoverColor: '{primary.400}',
      checkedColor: '{primary.400}',
    },
  },
  stepper: {
    stepNumber: {
      activeBackground: '{blue.50}',
    },
  },
  tabs: {
    activeBar: {
      height: '2px',
    },
  },
}

export const install: UserModule = (app) => {
  const MyPreset = definePreset(Aura, {
    primitive: {
      indigo: {
        50: '#f2f2ff',
        100: '#e6e6ff',
        200: '#bfbfff',
        300: '#9999ff',
        400: '#4d4dff',
        500: '#151558',
        600: '#12124c',
        700: '#0e0e40',
        800: '#0b0b33',
        900: '#080827',
        950: '#070722',
      },
      orange: {
        50: ' #fffdf5',
        100: ' #fff9e6',
        200: ' #fff2bf',
        300: ' #ffe999',
        400: ' #ffd94d',
        500: ' #f7b500',
        600: ' #d99d00',
        700: ' #b98a00',
        800: ' #997600',
        900: '#806300',
      },
      teal: {
        50: '#f0f9f8',
        100: '#d5f0eb',
        200: '#b9e6de',
        300: '#9ddcd1',
        400: '#82d2c4',
        500: '#abd3cc',
        600: '#5eb7a9',
        700: '#4b9a8c',
        800: '#387d6f',
        900: '#265f52',
      },
      emerald: {
        50: '#ecfdf5',
        100: '#d1fae5',
        200: '#a7f3d0',
        300: '#6ee7b7',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
        800: '#065f46',
        900: '#064e3b',
        950: '#022c22',
      },
      /*
       * Alias green -> emerald so PrimeVue's built-in `severity="success"`
       * components (Button/Tag/Message/…), which the Aura preset hardcodes to
       * `{green.*}`, follow the emerald success color. Emerald above is the
       * single source of truth.
       */
      green: {
        50: '{emerald.50}',
        100: '{emerald.100}',
        200: '{emerald.200}',
        300: '{emerald.300}',
        400: '{emerald.400}',
        500: '{emerald.500}',
        600: '{emerald.600}',
        700: '{emerald.700}',
        800: '{emerald.800}',
        900: '{emerald.900}',
        950: '{emerald.950}',
      },
      /*
       * Alias sky -> blue so PrimeVue's built-in `severity="info"` components,
       * which the Aura preset hardcodes to `{sky.*}`, match the `info` UnoCSS
       * token (`--p-blue-*`). Without this, `<Button severity="info">` renders
       * sky while `text-info-*` utilities render blue.
       */
      sky: {
        50: '{blue.50}',
        100: '{blue.100}',
        200: '{blue.200}',
        300: '{blue.300}',
        400: '{blue.400}',
        500: '{blue.500}',
        600: '{blue.600}',
        700: '{blue.700}',
        800: '{blue.800}',
        900: '{blue.900}',
        950: '{blue.950}',
      },
    },
    semantic: {
      primary: {
        50: '{indigo.50}',
        100: '{indigo.100}',
        200: '{indigo.200}',
        300: '{indigo.300}',
        400: '{indigo.400}',
        500: '{indigo.500}',
        600: '{indigo.600}',
        700: '{indigo.700}',
        800: '{indigo.800}',
        900: '{indigo.900}',
        950: '{indigo.950}',
      },
      form: {
        field: {
          padding: {
            y: '0.75rem',
            x: '1rem',
          },
        },
      },
      button: {
        sm: {
          padding: {
            y: '0.75rem',
            x: '1rem',
          },
        },
        lg: {
          padding: {
            y: '0.75rem',
            x: '1rem',
          },
        },
      },
    },
    components: {
      ...componentTokens,
    },
  })
  const config: PrimeVueConfiguration = {
    theme: {
      preset: MyPreset,
      options: {
        darkModeSelector: '.app-dark',
        cssLayer: {
          name: 'primevue',
          order: 'tailwind-base, primevue, tailwind-utilities',
        },
      },
    },
    ripple: true,
    pt: PrimeVuePassThroughOptions,
  }

  app.use(ToastService)
  app.use(DialogService)
  app.use(ConfirmationService)
  app.directive('ripple', Ripple)
  app.use(PrimeVue, config)
}
